import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  qualityCertifications,
  qualityHighlights,
  qualityLabs,
} from "@/mocks/quality";

const eyebrow = "text-[14px] tracking-[0.25em] uppercase text-primary-600 mb-3";
const h2 =
  "font-heading text-3xl md:text-5xl !leading-[1.3] text-foreground-950 mb-5";
const btnGradient =
  "group inline-flex rounded-full bg-gradient-to-br from-primary-300 via-primary-500 to-accent-400 p-px whitespace-nowrap cursor-pointer";
const btnGradientInner =
  "inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-foreground-950 text-xs tracking-widest uppercase transition-colors group-hover:bg-gradient-to-br group-hover:from-primary-500 group-hover:via-primary-600 group-hover:to-accent-500 group-hover:text-background-50";

type Highlight = { title: string; desc: string };
type Step = { step: string; title: string; desc: string };
type SpecRow = { param: string; standard: string; method: string };
type Stat = { value: string; label: string };
type Labeled = { title: string; desc?: string };

export function QualityHero() {
  const { t } = useTranslation();
  return (
    <section className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
      <img
        src="https://public.readdy.ai/ai/img_res/edited_4855fe6efc38793bef7516775321b4d0_1774e252.jpg"
        alt="Steaming bowl of freshly cooked Vietnamese rice"
        title="Quality at EverRice"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50/88 via-background-50/55 to-transparent" />
      <div className="relative h-full flex items-center page-gutter">
        <div className="max-w-[37.5rem] fade-up pt-16">
          <p className="font-heading text-lg tracking-[0.3em] text-foreground-700 mb-4">
            {t("qualityPage.hero.eyebrow")}
          </p>
          <h1 className="font-heading text-4xl md:text-6xl !leading-[1.3] tracking-[0.03em] text-foreground-950 mb-6">
            {t("qualityPage.hero.title")}
          </h1>
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed">
            {t("qualityPage.hero.desc")}
          </p>
        </div>
      </div>
    </section>
  );
}

export function QualityIntro() {
  const { t } = useTranslation();
  const highlights = t("qualityPage.intro.highlights", {
    returnObjects: true,
  }) as unknown as Highlight[];
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className={eyebrow}>{t("qualityPage.intro.eyebrow")}</p>
          <h2 className={h2}>{t("qualityPage.intro.title")}</h2>
          <p className="text-sm text-foreground-700 leading-relaxed max-w-lg mb-8">
            {t("qualityPage.intro.desc")}
          </p>
          <div className="space-y-5">
            {highlights.map((h, i) => (
              <div key={h.title} className="flex gap-4">
                <div className="w-12 h-12 shrink-0 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
                  <i className={`${qualityHighlights[i].icon} text-xl`} />
                </div>
                <div>
                  <h3 className="font-heading text-xl mb-1">{h.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed max-w-md">
                    {h.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full h-72 md:h-[460px] rounded-lg overflow-hidden">
          <img
            src="https://storage.helloreaddy.io/project_files/35136070-61cb-45a9-b929-229bbdd5ade8/3caeae4b-8e38-4538-aa8d-d399c8f03d6f_compressed_phong-thu-nghiem.webp"
            alt="Automated rice testing and quality inspection conveyor line"
            title="Quality control at EverRice"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}

export function QualityProcess() {
  const { t } = useTranslation();
  const items = t("qualityPage.process.items", {
    returnObjects: true,
  }) as unknown as Step[];
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("qualityPage.process.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("qualityPage.process.title")}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {items.map((s, i) => (
          <div
            key={s.step}
            className={`rounded-lg p-6 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${i === 2 ? "bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 shadow-lg" : "bg-background-50 border border-background-200 shadow-md"}`}
          >
            <span
              className={`font-heading text-4xl mb-3 ${i === 2 ? "text-background-50" : "text-primary-500"}`}
            >
              {s.step}
            </span>
            <h3 className="text-xs tracking-widest uppercase font-medium mb-2">
              {s.title}
            </h3>
            <p
              className={`text-xs leading-relaxed ${i === 2 ? "text-background-100" : "text-foreground-600"}`}
            >
              {s.desc}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function QualitySpecs() {
  const { t } = useTranslation();
  const head = t("qualityPage.specs.head", {
    returnObjects: true,
  }) as unknown as { param: string; standard: string; method: string };
  const rows = t("qualityPage.specs.rows", {
    returnObjects: true,
  }) as unknown as SpecRow[];
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <p className={eyebrow}>{t("qualityPage.specs.eyebrow")}</p>
          <h2 className={`${h2} mb-0`}>{t("qualityPage.specs.title")}</h2>
        </div>
        <p className="text-sm text-foreground-700 max-w-sm leading-relaxed">
          {t("qualityPage.specs.sub")}
        </p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-background-200">
        <table className="w-full min-w-[560px] text-left bg-background-100">
          <thead>
            <tr className="bg-primary-100 text-[11px] tracking-widest uppercase text-foreground-800">
              <th className="px-6 py-4 font-medium">{head.param}</th>
              <th className="px-6 py-4 font-medium">{head.standard}</th>
              <th className="px-6 py-4 font-medium">{head.method}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.param}
                className={`text-sm border-t border-background-200 ${i % 2 === 1 ? "bg-background-50" : ""}`}
              >
                <td className="px-6 py-4 font-medium text-foreground-900">
                  {r.param}
                </td>
                <td className="px-6 py-4 text-foreground-700">{r.standard}</td>
                <td className="px-6 py-4 text-foreground-600 text-xs tracking-wider uppercase">
                  {r.method}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function QualityLabs() {
  const { t } = useTranslation();
  const items = t("qualityPage.labs.items", {
    returnObjects: true,
  }) as unknown as Labeled[];
  return (
    <section className="bg-background-100 page-gutter py-16 md:py-24">
      <div className="text-center mb-12">
        <p className={eyebrow}>{t("qualityPage.labs.eyebrow")}</p>
        <h2 className={`${h2} mb-0`}>{t("qualityPage.labs.title")}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((l, i) => (
          <div
            key={l.title}
            className="rounded-lg overflow-hidden border border-background-200 bg-background-50 group cursor-pointer"
          >
            <div className="w-full h-56 overflow-hidden">
              <img
                src={qualityLabs[i].img}
                alt={l.title}
                title={`${l.title} - EverRice`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-xl">{l.title}</h3>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function QualityCertifications() {
  const { t } = useTranslation();
  const items = t("qualityPage.certs.items", {
    returnObjects: true,
  }) as unknown as Highlight[];
  return (
    <section className="bg-primary-50 page-gutter py-16 md:py-24">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10 items-center">
        <div>
          <p className={eyebrow}>{t("qualityPage.certs.eyebrow")}</p>
          <h2 className={h2}>{t("qualityPage.certs.title")}</h2>
          <p className="text-sm text-foreground-700 leading-relaxed">
            {t("qualityPage.certs.desc")}
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((c, i) => (
            <div
              key={c.title}
              className={`rounded-lg p-6 text-center transition-transform duration-300 hover:-translate-y-1 ${i === 1 ? "bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 shadow-lg" : "bg-background-50 border border-background-200 shadow-md"}`}
            >
              <div
                className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${i === 1 ? "bg-background-50/20" : "bg-primary-100"}`}
              >
                <i
                  className={`${qualityCertifications[i].icon} text-2xl ${i === 1 ? "text-background-50" : "text-primary-600"}`}
                />
              </div>
              <h3 className="text-xs tracking-widest uppercase font-medium mb-2">
                {c.title}
              </h3>
              <p
                className={`text-xs leading-relaxed ${i === 1 ? "text-background-100" : "opacity-80"}`}
              >
                {c.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function QualityStats() {
  const { t } = useTranslation();
  const stats = t("qualityPage.stats", {
    returnObjects: true,
  }) as unknown as Stat[];
  return (
    <section className="bg-background-50 page-gutter py-14">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={`rounded-lg p-6 text-center transition-transform duration-300 hover:-translate-y-1 ${i === 1 ? "bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 shadow-lg" : "bg-background-50 border border-background-200 shadow-md"}`}
          >
            <p className="font-heading text-4xl md:text-5xl">{s.value}</p>
            <p
              className={`text-xs tracking-widest uppercase mt-2 ${i === 1 ? "text-background-100" : "text-foreground-800"}`}
            >
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function QualityCta() {
  const { t } = useTranslation();
  return (
    <section className="bg-background-50 page-gutter py-16 md:py-24">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 rounded-lg bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 text-background-50 px-6 md:px-14 py-10">
        <div>
          <h2 className="font-heading text-2xl md:text-4xl mb-3">
            {t("qualityPage.cta.title")}
          </h2>
          <p className="text-sm opacity-90 max-w-xl leading-relaxed">
            {t("qualityPage.cta.desc")}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <Link
            to="/contact#send-message"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-background-50 text-foreground-950 text-xs tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
          >
            {t("qualityPage.cta.ctaContact")}{" "}
            <i className="ri-arrow-right-line" />
          </Link>
          <Link to="/products" className={btnGradient}>
            <span className={btnGradientInner}>
              {t("qualityPage.cta.ctaProducts")} <i className="ri-leaf-line" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
